import { useEffect, useState } from 'react';
import { API_BASE } from './api-base';
import { HIDE_DRAWER_PROGRESS_BAR_SPEC, toggleOverride } from './dev-feature-toggles';

/**
 * Client read of the DRAWER credits card's upsell arm — `symphonyHideDrawerProgressBarWeb`.
 *
 * Variant (`true`): the card drops its usage sentence and progress bar for a one-line upsell subtitle,
 * and the CTA becomes a full-width button at the foot (Figma 15375-105152 / 15375-105248).
 * Control (`false`): the card exactly as it ships.
 *
 * It FAILS CLOSED, and here that means CONTROL — the shipped card. This gate sits on the rail every
 * user sees on every page, so the failure that costs nothing is the one that shows what already works;
 * an accidental always-on would move the whole userbase onto an unmeasured card at once.
 *
 * A dev-tools **Feature toggles** override wins over the conducted value, so forcing it On/Off
 * exercises the real card rather than a special case. That override is inert without a dev-tools grant
 * (lib/dev-feature-toggles.ts), so a real user always gets the conducted value.
 *
 * The phones conduct their own spec for the same change (`symphonyHideDrawerProgressBarNative`) — see the
 * note on the spec constant.
 */

/**
 * How long the rail will wait for the arm before settling on control.
 *
 * Nothing BLOCKS on this gate — the hook starts at control and swaps if the variant lands — so the bound
 * is not about paint time the way the picker's is. It is about the cache: `cached` is memoized for the
 * page load, so a fetch that hangs and resolves a minute later would flip the card under a user
 * mid-session, which is both a worse experience than either arm and a reading in both arms. Aborting
 * makes "did not answer promptly" mean the same thing as "answered control", permanently.
 *
 * The route bounds its own `conduct` at 3s but leaves `getUserData` unbounded, so it cannot be relied on
 * to answer at all. 1.5s matches the sibling gate.
 *
 * ⚠️ **KNOWN, DELIBERATE, AND QUEUED — this bound is SHORTER than the route's own 3s conduct**, so a
 * conduct that lands between 1.5s and 3s reads as control here while Petri has already recorded the
 * exposure. That is exposure without treatment, i.e. it dilutes the experiment rather than merely
 * costing a user the variant.
 *
 * It is left as-is on purpose: `billing-cycle-step` and `earn-credits-offer` have exactly the same
 * skew, and fixing this one gate alone would make it the odd one out of the three while the underlying
 * cause went untouched. **The fix belongs to all three gates at once, and the client bound is the
 * smaller half of it** — the route's UNBOUNDED `getUserData` is what actually makes it unable to
 * answer, so raising this number without bounding that leg just waits longer on the same hang. Do both,
 * for all three, in a change of their own — do not treat this as a documented quirk to preserve.
 */
const GATE_TIMEOUT_MS = 1500;

let cached: Promise<boolean> | null = null;

export function hideDrawerProgressBarEnabled(): Promise<boolean> {
  // Checked on every call, not memoized: the override can be set mid-session from the dev panel.
  const forced = toggleOverride(HIDE_DRAWER_PROGRESS_BAR_SPEC);
  if (forced !== undefined) return Promise.resolve(forced === 'true');
  if (cached) return cached;
  cached = (async () => {
    try {
      const res = await fetch(`${API_BASE}/api/flags/hide-drawer-progress-bar`, {
        credentials: 'include',
        signal: AbortSignal.timeout(GATE_TIMEOUT_MS),
      });
      if (!res.ok) return false;
      return ((await res.json()) as boolean) === true;
    } catch {
      // Includes the abort: a gate that has not answered in time IS control.
      return false;
    }
  })();
  return cached;
}

/**
 * Resolve the arm for rendering. Starts false — the CONTROL card — so a user who is not in the variant
 * never sees the upsell card flash in and out. The cost of that choice is the reverse for a user who IS
 * in it: they get one frame of the control card. That direction is deliberate; the alternative shows the
 * variant to everyone for a moment, which is a worse thing to be wrong about on a shared rail.
 */
export function useHideDrawerProgressBarEnabled(): boolean {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    let live = true;
    void hideDrawerProgressBarEnabled().then((on) => {
      if (live) setEnabled(on);
    });
    return () => {
      live = false;
    };
  }, []);
  return enabled;
}
